import { Component } from "react";
import { paletteColor } from "@/lib/palette";

// A deploy changes the hashed chunk filenames. A returning visitor whose tab
// still points at the old index.html then fails to lazy-load a route chunk →
// without a boundary, React unmounts everything and the page goes blank. This
// boundary catches that (and any render error): on a chunk error it reloads once
// to fetch the fresh app; otherwise it shows a graceful "reload" screen instead
// of a white page. The fallback uses no context/hooks, so it survives even if a
// provider is what threw.
const RELOAD_KEY = "provena_chunk_reload";

const isChunkError = (error) => {
  const msg = `${error?.name || ""} ${error?.message || error || ""}`;
  return /ChunkLoadError|Loading chunk|dynamically imported|Failed to fetch dynamically/i.test(msg);
};

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error) {
    // Stale bundle after a deploy → reload once to pull the new chunks.
    if (isChunkError(error)) {
      try {
        if (!sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, "1");
          window.location.reload();
        }
      } catch {
        window.location.reload();
      }
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "1.25rem",
          background: paletteColor("paper"),
          color: paletteColor("ink-soft"),
          fontFamily: "system-ui, sans-serif",
          padding: "2rem",
          textAlign: "center",
        }}
      >
        <img
          src="/provena-logo-clair.svg"
          alt="Provena Registry"
          style={{ height: 64, width: "auto", objectFit: "contain" }}
        />
        <p style={{ maxWidth: 420, lineHeight: 1.6, color: paletteColor("ink-muted") }}>
          Une erreur est survenue. Rechargez la page pour continuer.
          <br />
          <span style={{ fontSize: 13, color: paletteColor("ink-subtle") }}>
            An error occurred. Please reload the page.
          </span>
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            border: `1px solid ${paletteColor("gold", 0.5)}`,
            color: paletteColor("gold-ink"),
            background: "transparent",
            padding: "0.75rem 2rem",
            textTransform: "uppercase",
            letterSpacing: "0.15em",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          Recharger / Reload
        </button>
      </div>
    );
  }
}
